const BASE = import.meta.env.VITE_API_BASE ?? "";

function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp("(^|;\\s*)" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[2]) : null;
}

async function toError(r: Response) {
  let detail: any = r.statusText;
  try { detail = await r.json(); } catch { /* non-JSON */ }
  const msg = detail?.detail || detail?.issues?.[0]?.message || detail?.validation?.issues?.[0]?.message || `HTTP ${r.status}`;
  return Object.assign(new Error(msg), { status: r.status, detail });
}

function headers(json = true): Record<string, string> {
  const h: Record<string, string> = {};
  if (json) h["Content-Type"] = "application/json";
  const csrf = getCookie("csrftoken");
  if (csrf) h["X-CSRFToken"] = csrf;
  return h;
}

/** fetch with an abort timeout so a cold/slow backend never hangs the app forever. */
async function fetchT(input: string, init: RequestInit = {}, timeoutMs = 20000): Promise<Response> {
  const ctrl = new AbortController();
  const id = window.setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: ctrl.signal });
  } finally {
    window.clearTimeout(id);
  }
}

/** Call once on app start so Django sets the csrftoken cookie. Never blocks the app. */
export async function ensureCsrf() {
  try { await fetchT(BASE + "/api/auth/csrf/", { credentials: "include" }, 15000); }
  catch { /* offline, slow, or cold backend — proceed */ }
}

export async function apiGet<T>(path: string): Promise<T> {
  const r = await fetchT(BASE + path, { credentials: "include" });
  if (!r.ok) throw await toError(r);
  return r.json() as Promise<T>;
}

/** GET a list endpoint, unwrapping DRF pagination to a plain array. Follows the `next`
 *  link so ALL pages are returned, not just the first — otherwise indicators/records
 *  beyond page 1 (e.g. the GDP/expenditure indicator) silently vanish from the UI.
 *  A safety cap prevents an accidental infinite loop. */
export async function apiList<T>(path: string): Promise<T[]> {
  const first = await apiGet<any>(path);
  if (Array.isArray(first)) return first;                 // non-paginated endpoint
  if (!first || !("results" in first)) return first?.results ?? [];

  let out: T[] = first.results ?? [];
  let next: string | null = first.next ?? null;
  let guard = 0;
  while (next && guard < 100) {
    guard += 1;
    // `next` is an absolute URL from DRF; strip the origin so it works behind the proxy.
    let nextPath = next;
    try { const u = new URL(next); nextPath = u.pathname + u.search; } catch { /* already relative */ }
    const page: any = await apiGet<any>(nextPath);
    out = out.concat(page.results ?? []);
    next = page.next ?? null;
  }
  return out;
}

export async function apiSend<T>(path: string, method: string, body?: unknown): Promise<T> {
  const r = await fetchT(BASE + path, {
    method, credentials: "include", headers: headers(),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!r.ok) throw await toError(r);
  return (r.status === 204 ? (undefined as T) : (r.json() as Promise<T>));
}

export async function apiUpload<T>(path: string, file: File): Promise<T> {
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetchT(BASE + path, { method: "POST", credentials: "include", headers: headers(false), body: fd }, 120000);
  if (!r.ok) throw await toError(r);
  return r.json() as Promise<T>;
}

export function wsUrl(path: string): string {
  const base = import.meta.env.VITE_WS_BASE || window.location.origin.replace(/^http/, "ws");
  return base + path;
}

export async function apiDownload(path: string, fallbackName: string) {
  const r = await fetchT(BASE + path, { credentials: "include" }, 120000);
  if (!r.ok) throw await toError(r);
  const blob = await r.blob();
  const cd = r.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="?([^"]+)"?/);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = m ? m[1] : fallbackName; a.click();
  URL.revokeObjectURL(url);
}
